import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { graphQueries } from '../../api/graph.api';
import { useEffect, useMemo } from 'react';
import { useFilterContext } from '../state';
import { ResourceType } from '../../api/app.types';
import { AppBaseDto } from '../../api/graph.types';
import useAdditionalAppIds from './useAdditionalAppIds';

export default function usePagedApps(resourceType?: ResourceType) {
  const { showAllApps } = useFilterContext();

  const appRegistrationsQueryOptions = showAllApps
    ? graphQueries.getAppRegistrations
    : graphQueries.getMyAppRegistrations;

  const enterpriseApplicationsQueryOptions = showAllApps
    ? graphQueries.getEnterpriseApplications
    : graphQueries.getMyEnterpriseApplications;

  const {
    data: getAppRegistrationResponse,
    hasNextPage: hasMoreAppRegistrations,
    isFetchingNextPage: isFetchingAppRegistrations,
    fetchNextPage: fetchMoreAppRegistrations,
    isPending: isPendingAppRegistrations,
  } = useInfiniteQuery({
    ...appRegistrationsQueryOptions,
    enabled: resourceType === ResourceType.AppRegistration || resourceType === undefined, // Don't query them if they are not required
  });

  const {
    data: getEnterpriseApplicationResponse,
    hasNextPage: hasMoreEnterpriseApplications,
    isFetchingNextPage: isFetchingEnterpriseApplications,
    fetchNextPage: fetchMoreEnterpriseApplications,
    isPending: isPendingEnterpriseApplications,
  } = useInfiniteQuery({
    ...enterpriseApplicationsQueryOptions,
    enabled: resourceType === ResourceType.EnterpriseApplication || resourceType === undefined, // Don't query them if they are not required
  });

  const { additionalAppRegistrationIds, additionalEnterpriseApplicationIds } = useAdditionalAppIds();

  const {
    data: additionalAppRegistrationsResult,
    refetch: refetchAdditionalAppRegistrations,
    isFetching: isFetchingAdditionalAppRegistrations,
    isError: isErrorAdditionalAppRegistrations,
  } = useQuery({
    ...graphQueries.getAppRegistrationsInBatch(additionalAppRegistrationIds),
    enabled: additionalAppRegistrationIds.length !== 0, // Enabled only if we got some additional apps to fetch
  });

  const {
    data: additionalEnterpriseApplicationsResult,
    refetch: refetchAdditionalEnterpriseApplications,
    isFetching: isFetchingAdditionalEnterpriseApplications,
    isError: isErrorAdditionalEnterpriseApplications,
  } = useQuery({
    ...graphQueries.getEnterpriseApplicationsInBatch(additionalEnterpriseApplicationIds),
    enabled: additionalEnterpriseApplicationIds.length !== 0, // Enabled only if we got some additional apps to fetch
  });

  const additionalAppRegistrations = additionalAppRegistrationsResult?.apps;
  const additionalEnterpriseApplications = additionalEnterpriseApplicationsResult?.apps;

  // A lookup where nothing loaded throws, and the query then keeps the apps it loaded before; all of its ids count as failed.
  const failedAppRegistrations = isErrorAdditionalAppRegistrations
    ? additionalAppRegistrationIds.length
    : (additionalAppRegistrationsResult?.failedIds.length ?? 0);
  const failedEnterpriseApplications = isErrorAdditionalEnterpriseApplications
    ? additionalEnterpriseApplicationIds.length
    : (additionalEnterpriseApplicationsResult?.failedIds.length ?? 0);

  const failedAdditionalAppCount = failedAppRegistrations + failedEnterpriseApplications;

  const retryAdditionalApps = () => {
    if (failedAppRegistrations > 0) void refetchAdditionalAppRegistrations();
    if (failedEnterpriseApplications > 0) void refetchAdditionalEnterpriseApplications();
  };

  // TODO: Support triggering fetchMore imperatively (e.g., upon reaching the bottom of the page)
  useEffect(() => {
    if (hasMoreAppRegistrations && !isFetchingAppRegistrations) {
      fetchMoreAppRegistrations();
    }
  }, [hasMoreAppRegistrations, isFetchingAppRegistrations, fetchMoreAppRegistrations]);

  useEffect(() => {
    if (hasMoreEnterpriseApplications && !isFetchingEnterpriseApplications) {
      fetchMoreEnterpriseApplications();
    }
  }, [hasMoreEnterpriseApplications, isFetchingEnterpriseApplications, fetchMoreEnterpriseApplications]);

  const appRegistrations = useMemo(() => {
    const apps = getAppRegistrationResponse?.pages.flatMap((page) => page.value).filter((app) => !!app) ?? [];

    if (additionalAppRegistrations) {
      for (const additionalAppRegistration of additionalAppRegistrations) {
        if (apps.some((app) => app.id === additionalAppRegistration.id)) continue;

        apps.push(additionalAppRegistration);
      }
    }

    return apps;
  }, [getAppRegistrationResponse, additionalAppRegistrations]);

  const enterpriseApplications = useMemo(() => {
    const apps = getEnterpriseApplicationResponse?.pages.flatMap((page) => page.value).filter((app) => !!app) ?? [];

    if (additionalEnterpriseApplications) {
      for (const additionalEnterpriseApplication of additionalEnterpriseApplications) {
        if (apps.some((app) => app.id === additionalEnterpriseApplication.id)) continue;

        apps.push(additionalEnterpriseApplication);
      }
    }

    return apps;
  }, [getEnterpriseApplicationResponse, additionalEnterpriseApplications]);

  const allApplications = useMemo(() => {
    return [...appRegistrations, ...enterpriseApplications].reduce((map, a) => {
      if (!map.has(a.id)) {
        map.set(a.id, {
          ...a,
          resourceType: appRegistrations.includes(a)
            ? ResourceType.AppRegistration
            : ResourceType.EnterpriseApplication,
        });
      }
      return map;
    }, new Map<string, AppBaseDto>());
  }, [appRegistrations, enterpriseApplications]);

  // TODO: Show a toast if the paged queries fail

  return {
    isPendingAppRegistrations,
    isPendingEnterpriseApplications,
    appRegistrations,
    enterpriseApplications,
    allApplications,
    /** How many additional apps (owned through settings or tasks) could not be read. 404s are not counted. */
    failedAdditionalAppCount,
    isRetryingAdditionalApps: isFetchingAdditionalAppRegistrations || isFetchingAdditionalEnterpriseApplications,
    retryAdditionalApps,
  };
}
